import React, { useEffect, useRef, useState } from 'react';
import { DomUtil } from '@lwtears/lwt-common-frontend/lib/@common';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SVG from '../../../../../common/SVG';
import { useFindTheObject } from '../../../../../../hooks/use-find-the-object';
import useActivityAudio from '../../../../../../hooks/use-activity-audio';
import useGame from '../../../../../../hooks/use-game';
import useHint from '../../../../../../hooks/use-hint';
import useOverlay from '../../../../../../hooks/use-overlay';
import usePage from '../../../../../../hooks/use-page';
import { FindTheObjectInterface } from '../../../../../../providers/GameProvider/types';
import { GameSounds } from '../../../../../../transformers/FindTheObjects';
import genericSounds from '../../../../../../transformers/genericSounds';
import useZoom from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/hooks/use-zoom';
import {
  isElementClicked,
  getRelativeCursorPosition,
  getIntAttribute,
} from '../../../../../../util/dom-util';
import { findObjectWithAnimation } from '../../../../../../util/find-the-object-util';
import {
  FOUND_OBJECT_STYLE,
  MIN_HITBOX_SIZE,
  buildHintCircle,
  removeHints,
} from '../../../../../../util/gameplay-util';
import { hasOngoingAction } from '../../../../../../util/has-ongoing-action';
import { first } from 'lodash';

interface FindTheObjectGameProps extends FindTheObjectInterface {
  activityContainerRef: any;
  onePageBehaviour: boolean;
  startingIndex: number;
  gameLoaded: boolean;
  onPageLoaded?: () => void;
}

const FindTheObjectGame: React.FC<FindTheObjectGameProps> = ({
  animation,
  svg,
  activityContainerRef,
  page,
  onePageBehaviour,
  startingIndex,
  gameLoaded,
  onPageLoaded,
}) => {
  const classes = useStyles({ gameLoaded });
  const { pageNumber } = page;

  const [{ objects, pages, hintTag }, { setFoundObjectTags }] = useFindTheObject();

  const [svgNode, setSVGNode] = useState<SVGSVGElement>();
  const [svgScaleFactor, setSVGScaleFactor] = useState<number>(1);

  const gameContainer = useRef<HTMLDivElement>(null);
  const animationPlayerRef = useRef<HTMLVideoElement>(null);
  const svgWrapperRef = useRef<HTMLDivElement>(null);

  const [{ isPlaying }, { playSound, stopSound }] = useActivityAudio();
  const [{ currentPage }] = usePage();
  const [{ showHint }] = useHint();
  const [{ showOverlay }] = useOverlay();
  const [{ isZooming, scale }] = useZoom();
  const [
    {
      activityState: { showAnimation },
    },
    { setShowAnimation },
  ] = useGame();

  const isEvenPageNumber = pageNumber % 2 === 0;

  useEffect(() => {
    if (activityContainerRef && svgNode && isValidSVGStructure(svgNode)) {
      buildFocusableItems();
    }

    objects.forEach(({ description }) => styleTargets(description, ''));

    removeHints(svgWrapperRef?.current);
    hintTag && showHint && showHints();

    pages[currentPage]?.foundObjectTags?.forEach((tag) => {
      styleTargets(tag, FOUND_OBJECT_STYLE);
    });

    return () => cleanupListeners();
  }, [
    svgNode,
    svgScaleFactor,
    activityContainerRef,
    pages[currentPage]?.foundObjectTags,
    showHint,
    hintTag,
    scale,
    isZooming,
  ]);

  const isValidSVGStructure = (svg) => !!svg; // TODO: validate received structure / nbr of objects

  const handleSvgLoaded = (scaleFactor: number, svgNode: SVGSVGElement | null) => {
    setSVGScaleFactor(scaleFactor);
    svgNode && setSVGNode(svgNode);
    onPageLoaded?.();
  };

  const showHints = () => {
    const exactMatchHints = svgWrapperRef?.current?.querySelectorAll(`[id='${hintTag}' i]`) ?? [];
    const startsWithHints = svgWrapperRef?.current?.querySelectorAll(`[id^='${hintTag}-' i]`) ?? [];

    const hints = exactMatchHints.length > 0 ? exactMatchHints : startsWithHints;

    hints.forEach((element) => {
      buildHintCircle(element, svgWrapperRef?.current, svgScaleFactor);
    });
  };

  // checks if target has a cue/description
  const getValidTargetTag = (element: SVGSVGElement): string | undefined => {
    const exactObject = objects.find(
      ({ description }) => element?.id.toLowerCase() === description.toLowerCase()
    )?.description;

    const startsWithObject = objects.find(({ description }) =>
      element?.id.toLowerCase().startsWith(`${description.toLowerCase()}-`)
    )?.description;

    return exactObject || startsWithObject;
  };

  const onTargetClick = (event) => {
    event.preventDefault();
    if (showHint || showAnimation || isZooming) return;
    if (isPlaying) stopSound();

    const cursorPosition = getRelativeCursorPosition(event, svgNode, svgScaleFactor);
    cursorPosition.clientX = cursorPosition.clientX / scale;
    cursorPosition.clientY = cursorPosition.clientY / scale;
    const targets = getClickedTargets(cursorPosition);

    const validTargets = targets?.reverse().filter((target) => getValidTargetTag(target));

    const frontmostUnselectedValidTarget = validTargets?.find((target) => !targetSelected(target));
    const frontmostSelectedValidTarget = first(validTargets);

    if (frontmostUnselectedValidTarget) {
      const targetTag = getValidTargetTag(frontmostUnselectedValidTarget);
      targetTag && onTargetHit(targetTag);
    } else if (frontmostSelectedValidTarget) {
      const targetTag = getValidTargetTag(frontmostSelectedValidTarget);
      targetTag && playTargetSound(targetTag);
    } else {
      onWrongTargetHit();
    }
  };

  const targetSelected = (target: SVGSVGElement): boolean =>
    !!target.getAttribute('style')?.includes('grayscale');

  const onTargetEnter = ({ target, key }) => {
    if (hasOngoingAction(showHint, showAnimation, isPlaying) || showOverlay) return;

    if (key === 'Enter') {
      const tag = getValidTargetTag(target);
      if (tag) {
        !targetSelected(target) ? onTargetHit(tag) : playTargetSound(tag);
      } else {
        onWrongTargetHit();
      }
    }
  };

  const onWrongTargetHit = () => {
    playSound(genericSounds.BOINK);
  };

  const playTargetSound = async (tag: string) => {
    const objectIndex = objects.findIndex(
      (object) => object.description.toLowerCase() === tag.toLowerCase()
    );

    if (objectIndex < 0) {
      return;
    }
    const audio = GameSounds.OBJECT_AUDIO(objects[objectIndex].description, objectIndex);

    if (animationPlayerRef.current && animation && objectHasAnimation(tag)) {
      await playTargetAnimation(audio);
    } else {
      await playSound(audio);
    }
  };

  const playTargetAnimation = async (audio): Promise<void> => {
    return new Promise((resolve) => {
      const videoPlayer = animationPlayerRef.current;
      if (videoPlayer && animation) {
        stopSound();
        setShowAnimation(true);
        videoPlayer.src = animation.src;
        videoPlayer.onended = () =>
          setTimeout(async () => {
            await playSound(audio);

            setShowAnimation(false);
            resolve();
          }, 100);
        videoPlayer.play().catch((err) => {
          console.log(err, 'err in playing video');
        });
      }
    });
  };

  const getClickedTargets = (cursorPosition): SVGSVGElement[] => {
    if (!svgNode) {
      return [] as SVGSVGElement[];
    }

    return (Array.from(svgNode.children) as SVGSVGElement[]).filter((elem) => {
      const faultTolerance = computeFaultToleranceForElement(elem, MIN_HITBOX_SIZE, svgScaleFactor);
      return isElementClicked(elem, cursorPosition, faultTolerance);
    });
  };

  const computeFaultToleranceForElement = (
    elem,
    minimumHitboxSize: number,
    scaleFactor: number
  ): { horizontal: number; vertical: number } => ({
    // all targets must have a min of 80px x 80px hitbox
    // return missing space to be added both horizontally and vertically, on both sides (hence / 2)
    // to obtain that min hitbox
    horizontal: Math.max(minimumHitboxSize * scaleFactor - getIntAttribute(elem, 'width'), 0) / 2,
    vertical: Math.max(minimumHitboxSize * scaleFactor - getIntAttribute(elem, 'height'), 0) / 2,
  });

  const onTargetHit = async (id: string) => {
    styleTargets(id, FOUND_OBJECT_STYLE);

    await playTargetSound(id);

    setTargetFound(id);
  };

  const getAllTargetsWithSameTag = (id: string): HTMLElement[] => {
    const exactElements = activityContainerRef?.current?.querySelectorAll(`[id='${id}' i]`);
    const startsWithElements = activityContainerRef?.current?.querySelectorAll(`[id*='${id}-' i]`);
    if (exactElements?.length) return exactElements;
    return startsWithElements;
  };

  const styleTargets = (id: string, styleProps: string) => {
    const targetsWithSameTag = getAllTargetsWithSameTag(id);

    targetsWithSameTag?.forEach((element) => {
      element.setAttribute('style', styleProps);
    });
  };

  const setTargetFound = (id: string) => {
    const targetsWithSameTag = getAllTargetsWithSameTag(id);

    if (targetsWithSameTag) {
      setFoundObjectTags(currentPage, id);
    }
  };

  const objectHasAnimation = (id: string): boolean => !!findObjectWithAnimation(objects, id);

  const buildFocusableItems = () => {
    if (!svgNode) {
      return;
    }

    setChildrenAsTargets(svgNode.children);

    svgNode.addEventListener('click', onTargetClick);
    svgNode.addEventListener('touchend', onTargetClick);
  };

  const setChildrenAsTargets = (children: HTMLCollection) => {
    (Array.from(children) as HTMLElement[]).forEach((elem, index) => {
      DomUtil.setManyAttributes([elem], {
        tabIndex: startingIndex + index,
        ariaLabel: `${elem.id}-image`,
      });
      elem.addEventListener('keyup', onTargetEnter);
    });
  };

  const cleanupListeners = () => {
    if (!svgNode) {
      return;
    }

    (Array.from(svgNode.children) as HTMLElement[]).forEach((elem) => {
      elem.removeEventListener('keyup', onTargetEnter);

      svgNode.removeEventListener('click', onTargetClick);
      svgNode.removeEventListener('touchend', onTargetClick);
    });
  };

  return (
    <Box className={classes.root}>
      <div
        id="game"
        className={`${classes.gameContainer} ${
          onePageBehaviour
            ? ''
            : isEvenPageNumber
            ? classes.paddedContainerLeft
            : classes.paddedContienrRight
        } ${showHint ? classes.defaultCursor : classes.pointerCursor}`}
      >
        <div ref={gameContainer} className={classes.fullSize}>
          <div
            style={{
              display: `${showAnimation ? 'block' : 'none'}`,
              zIndex: showAnimation ? 1 : 0,
            }}
            className={`${classes.fullSize} ${classes.animation} ${
              onePageBehaviour ? '' : classes.paddedAnimation
            }`}
          >
            <video ref={animationPlayerRef} style={{ maxHeight: '100%', maxWidth: '100%' }} />
          </div>
          {svg && (
            <SVG
              svgSrc={svg.src}
              onLoaded={handleSvgLoaded}
              parentRef={gameContainer}
              svgWrapperRef={svgWrapperRef}
              scale={scale}
            />
          )}
        </div>
      </div>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: ({ gameLoaded }: any) => (gameLoaded ? 'min-content' : '100%'),
    height: ({ gameLoaded }: any) => (gameLoaded ? 'min-content' : '100%'),
    display: 'flex',
    flexDirection: 'column',
  },
  paddedContainerLeft: {
    paddingLeft: theme.spacing(6),
    paddingRight: '2.54vh',
    paddingTop: '4vh',
  },
  paddedContienrRight: {
    paddingLeft: '2.54vh',
    paddingRight: theme.spacing(6),
    paddingTop: '4vh',
  },
  animation: {
    position: 'absolute',
  },
  fullSize: {
    height: '100%',
    width: 'auto',
    maxHeight: '100%',
    maxWidth: '100%',
  },
  paddedAnimation: {
    width: `calc(100% - (${theme.spacing(6)} + 2.54vh))`, // subtract the paddedContainer padding values
    height: `calc(100% - 4vh)`,
  },
  gameContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    '& *:focus': {
      outline: `${theme.spacing(3)} solid ${theme.palette.primary.main}}`,
      borderRadius: '50%',
    },
  },
  pointerCursor: {
    cursor: 'pointer',
  },
  defaultCursor: {
    cursor: 'default',
  },
}));

export default FindTheObjectGame;
